import { snakeCase } from "lodash";
import {
  useState,
  useEffect,
  SyntheticEvent,
  ChangeEvent,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { CaseAdmin, Department, Lab, LabOrder, Stain } from "pwapi";

import { getLabs } from "shared/actions/labActions";
import {
  fetchLabOrders,
  adminLabOrderPrintRequest,
} from "shared/actions/labOrdersActions";
import { selectFeatureFlag } from "shared/reducers/sysReducer";
import { displayPriority } from "shared/utils/caseUtils";
import { formatTimestampForDateTime } from "shared/utils/dates";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import { ControlledToggleButton } from "components/customInputs/buttons/ToggleButton";
import DateRange from "components/customInputs/DateRangeInput";
import SelectInput from "components/customInputs/SelectInput";
import TextInput from "components/customInputs/TextInput";
import {
  FilterObject,
  reduceFiltersForSearchParams,
  reduceFiltersToApi,
  resetFilters,
} from "components/filtersUtils";
import { useAppSelector } from "store/hooks";

import AdminPagination, {
  AdminPaginationProps,
} from "../casesTable/Pagination";
import { FilterComponentProps } from "../types/adminTypes";

const DoubleUnderscoreFilterMatch = {
  caseAccession: "case__accession",
  casePriority: "case__priority",
  caseAccessioningLabName: "case__accessioning_lab__name",
  labStainStainCategory: "lab_stain__stain__category",
  labStainExternalStainExternalLabName:
    "lab_stain__external_stain__external_lab__name",
};

// This is a list of external labs which are to be weighted to be put at the top of the external lab filter
// This should maintain the order of the labs, hence the weight property.
export const weightedExternalLabs: { [key: string]: number } = {
  propath: 10,
  "intermountain lab": 9,
  "neogenomics ca": 8,
  "neogenomics fl": 7,
  "neogenomics tx": 6,
  "arizona dermatopathology": 5,
  "global pathology": 4,
  "twin cities dermatopathology": 3,
  "vitro molecular labs": 2,
};

type QueryFilter = {
  [key: string]: boolean | null;
};

type DepartmentWithLab = Department & { lab: string };

const OrdersFilters = ({
  sortBy,
  pageSize,
  pageIndex,
  setPage,
  adminPaginationProps,
  isLoading = false,
}: FilterComponentProps & {
  adminPaginationProps?: AdminPaginationProps;
  isLoading?: boolean;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const canPrintLabOrders = useAppSelector((state) =>
    selectFeatureFlag(state, "canPrintLabOrders")
  );

  const labsState = useAppSelector((state) => state.labs);
  const labs = useMemo(
    () =>
      labsState?.results &&
      [...labsState.results].sort((labA: Lab, labB: Lab) =>
        labA.name > labB.name ? 1 : -1
      ),
    [labsState]
  );
  const externalLabsState = useAppSelector((state) => state.externalLabs);
  const externalLabs = useMemo(
    () =>
      externalLabsState &&
      Object.values(externalLabsState).sort((labA: Lab, labB: Lab) => {
        const labA_lc = labA.name.toLowerCase();
        const labB_lc = labB.name.toLowerCase();
        const weightCheck =
          (weightedExternalLabs[labB_lc] || 0) -
          (weightedExternalLabs[labA_lc] || 0);
        return weightCheck !== 0 ? weightCheck : labA_lc > labB_lc ? 1 : -1;
      }),
    [externalLabsState]
  );

  const departments = useMemo(() => {
    const allDepartments: DepartmentWithLab[] = [];
    const processLab = (lab: Lab) => {
      if (lab.departments && lab.departments.length > 0) {
        [...lab.departments]
          .sort(
            (a: any, b: any) =>
              (a.order_priority ?? 0) - (b.order_priority ?? 0)
          )
          .forEach((dept: any) => {
            allDepartments.push({
              ...dept,
              lab: lab.name,
            });
          });
      }
    };

    if (labsState?.results) {
      [...labsState.results]
        .sort((labA: Lab, labB: Lab) => (labA.name > labB.name ? 1 : -1))
        .forEach(processLab);
    }

    return allDepartments;
  }, [labsState]);

  useEffect(() => {
    !labsState && dispatch(getLabs({ limit: 9999, offset: 0 }));
  }, []);

  const usp = new URLSearchParams(location.search);
  const initializeFilters = (filters: FilterObject) => {
    return Object.keys(filters).reduce((acc: FilterObject, value) => {
      const initialFilter = usp.get(value);
      acc[value] = initialFilter ? initialFilter : "";
      return acc;
    }, {});
  };

  const [ordersFilters, setOrdersFilters] = useState<FilterObject>(
    initializeFilters({
      createdAtBefore: "",
      createdAtAfter: "",
      completedAtBefore: "",
      completedAtAfter: "",
      cancelledAtBefore: "",
      cancelledAtAfter: "",
      caseAccession: "",
      casePriority: "",
      caseAccessioningLabName: "",
      department: "",
      orderingDoctorName: "",
      type: "",
      labStainStainCategory: "",
      labStainExternalStainExternalLabName: "",
      outgoingSendoutLabs: "",
    })
  );

  const [queryFilters, setQueryFilters] = useState<{
    isUnfulfilled: boolean;
    // True = is a sendout, False = is in-house, null = not used
    isSendout: boolean | null;
    isNotPrinted: boolean;
  }>({
    isUnfulfilled: usp.get("isUnfulfilled") === "true",
    isSendout: usp.get("isSendout") ? usp.get("isSendout") === "true" : null,
    isNotPrinted: usp.get("isNotPrinted") === "true",
  });

  const ranFilters = useRef<boolean>(false);

  useEffect(() => {
    if (filledFilters) ranFilters.current = true;
    filterOrders(ordersFilters, queryFilters);
  }, [sortBy, pageSize, pageIndex]);

  useEffect(() => {
    setSearchParams(ordersFilters, queryFilters);
  }, [sortBy, pageIndex]);

  const onChangeFilter = (key: string, value: string) => {
    setOrdersFilters({ ...ordersFilters, [key]: value });
  };

  const onChangeDateRangeFilter = (values: { [key: string]: Date }) => {
    setOrdersFilters(Object.assign({}, ordersFilters, values));
  };

  const onChangeQueryFilter = (
    filter: "isUnfulfilled" | "isSendout" | "isNotPrinted"
  ) => {
    setQueryFilters({
      ...queryFilters,
      [filter]: !queryFilters[filter],
    });
  };

  const setSearchParams = (
    filters: FilterObject,
    queryFilters: QueryFilter
  ) => {
    const params = {
      ...reduceFiltersForSearchParams(filters),
      ...(queryFilters.isUnfulfilled && { isUnfulfilled: true }),
      ...(queryFilters.isSendout !== null && {
        isSendout: queryFilters.isSendout,
      }),
      ...(queryFilters.isNotPrinted && { isNotPrinted: true }),
    } as any;
    if (pageIndex > 0) {
      params.page = pageIndex + 1;
    }
    if (sortBy.length) {
      params.sortBy = sortBy.map((s) => `${s.desc ? "-" : ""}${s.id}`);
    }
    navigate(`${location.pathname}?${new URLSearchParams(params).toString()}`, {
      replace: true,
    });
  };

  const filterOrders = (filters: FilterObject, queryFilters: QueryFilter) => {
    dispatch(
      fetchLabOrders({
        filters: {
          limit: pageSize,
          offset: pageSize * pageIndex,
          ordering: sortBy.length
            ? sortBy[0].id === "case_priority"
              ? "-case__priority,-case__patient__clinic__tat_priority,-created_at"
              : `${sortBy[0].desc ? "-" : ""}${sortBy[0].id}`
            : "-created_at",
          ...reduceFiltersToApi(filters),
          ...(queryFilters.isUnfulfilled && {
            isCompleted: false,
            isCancelled: false,
          }),
          ...(queryFilters.isSendout !== null && {
            isSendout: queryFilters.isSendout,
          }),
          ...(queryFilters.isNotPrinted && {
            isPrinted: false,
          }),
          isSent: true,
        },
      })
    );
  };

  const submitOrdersToPrint = useCallback(() => {
    const apiFilters = reduceFiltersToApi(ordersFilters);
    const snakeCaseFilters = Object.keys(apiFilters).reduce((acc, key) => {
      const keyName =
        DoubleUnderscoreFilterMatch[
          key as keyof typeof DoubleUnderscoreFilterMatch
        ] || snakeCase(key);
      return { ...acc, [keyName]: apiFilters[key] };
    }, {});
    const querySnakeFilters = {
      ...(queryFilters.isUnfulfilled && {
        is_completed: false,
        is_cancelled: false,
      }),
      ...(queryFilters.isSendout !== null && {
        is_sendout: queryFilters.isSendout,
      }),
      ...(queryFilters.isNotPrinted && {
        is_printed: false,
      }),
      is_sent: true,
    };
    dispatch(
      adminLabOrderPrintRequest({
        requestBody: {
          filter_criteria: { ...snakeCaseFilters, ...querySnakeFilters },
        },
      })
    ).then((response: any) => {
      navigate(`/admin/orders/print/${response.id}`);
    });
  }, [ordersFilters, queryFilters]);

  const onSubmitFilter = (e: SyntheticEvent) => {
    e.preventDefault();
    setSearchParams(ordersFilters, queryFilters);
    filterOrders(ordersFilters, queryFilters);
    ranFilters.current = true;
    setPage && setPage(0);
  };

  const onResetFilter = () => {
    const newFilters = resetFilters(ordersFilters);
    setOrdersFilters(newFilters);
    setQueryFilters({
      isNotPrinted: false,
      isUnfulfilled: false,
      isSendout: null,
    });
    setSearchParams(newFilters, queryFilters);
    filterOrders(newFilters, queryFilters);
    ranFilters.current = false;
    setPage && setPage(0);
  };

  const filledFilters = useMemo(() => {
    if (queryFilters.isSendout === false) return true;

    return (
      Object.values(queryFilters).some((query) => query) ||
      Object.values(ordersFilters).some((filter) => filter)
    );
  }, [ordersFilters, queryFilters]);

  return (
    <>
      <form className="AdminFilters" onSubmit={onSubmitFilter}>
        <div className="AdminFilters AdminFilters--vertical">
          <div className="AdminFilters__filters">
            <TextInput
              id="caseAccession"
              value={ordersFilters.caseAccession as string}
              label="Case Accession"
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onChangeFilter("caseAccession", e.target.value)
              }
            />
            <SelectInput
              id="casePriority"
              value={ordersFilters.casePriority as string}
              label="Case Priority"
              defaultLabel="All Priorities..."
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                onChangeFilter("casePriority", e.target.value)
              }
              options={Object.keys(CaseAdmin.priority).map((priority) => ({
                label: displayPriority(priority),
                value:
                  CaseAdmin.priority[
                    priority as keyof typeof CaseAdmin.priority
                  ],
              }))}
            />
            <SelectInput
              id="type"
              value={ordersFilters.type as string}
              label="Order Type"
              defaultLabel="All Types..."
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                onChangeFilter("type", e.target.value)
              }
              options={Object.keys(LabOrder.type).map((type) => ({
                label: type,
                value: LabOrder.type[type as keyof typeof LabOrder.type],
              }))}
            />
            <SelectInput
              id="labStainStainCategory"
              value={ordersFilters.labStainStainCategory as string}
              label="Stain Category"
              defaultLabel="All Categories..."
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                onChangeFilter("labStainStainCategory", e.target.value)
              }
              options={Object.keys(Stain.category)
                .filter(
                  (category) =>
                    !category.startsWith("CED_") && !category.startsWith("PW_")
                )
                .map((category) => ({
                  label: category,
                  value:
                    Stain.category[category as keyof typeof Stain.category],
                }))}
            />
            <TextInput
              id="orderingDoctorName"
              value={ordersFilters.orderingDoctorName as string}
              label="Ordering User"
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onChangeFilter("orderingDoctorName", e.target.value)
              }
            />
            <SelectInput
              id="isSendout"
              value={
                queryFilters.isSendout === null
                  ? ""
                  : queryFilters.isSendout
                  ? "true"
                  : "false"
              }
              label={"Order Destination"}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                setQueryFilters({
                  ...queryFilters,
                  isSendout:
                    e.target.value === ""
                      ? null
                      : e.target.value === "true"
                      ? true
                      : false,
                })
              }
              defaultLabel="All Destinations..."
              options={[
                {
                  label: "Sendout",
                  value: "true",
                },
                {
                  label: "In-House",
                  value: "false",
                },
              ]}
            />
            {/* Lab sending out the order */}
            <SelectInput
              id="outgoingSendoutsLab"
              label="Sendout Lab"
              defaultLabel="All Labs..."
              value={ordersFilters.outgoingSendoutsLab as string}
              options={
                labs?.map((v: Lab) => ({ label: v.name, value: v.name })) || []
              }
              onChange={(e: any) =>
                onChangeFilter("outgoingSendoutsLab", e.target.value)
              }
            />
            {/* Lab performing the work on the order from a sendout */}
            <SelectInput
              id="labStainExternalStainExternalLabName"
              label="Performing Lab"
              name="Performing Lab"
              defaultLabel="All Labs..."
              value={
                ordersFilters.labStainExternalStainExternalLabName as string
              }
              options={
                externalLabs?.map((v: Lab) => ({
                  label: v.name,
                  value: v.name,
                })) || []
              }
              onChange={(e: any) =>
                onChangeFilter(
                  "labStainExternalStainExternalLabName",
                  e.target.value
                )
              }
            />
            <SelectInput
              id="caseAccessioningLabName"
              label="Accessioning Lab"
              defaultLabel="All Labs..."
              value={ordersFilters.caseAccessioningLabName as string}
              options={
                labs?.map((v: Lab) => ({ label: v.name, value: v.name })) || []
              }
              onChange={(e: any) =>
                onChangeFilter("caseAccessioningLabName", e.target.value)
              }
            />
            <div className="AdminFilters--span">
              <DateRange
                id="createdAt"
                label="Order Placed At"
                type="datetime-local"
                values={{
                  from: {
                    id: "createdAtAfter",
                    name: "created_at_after",
                    value: ordersFilters.createdAtAfter
                      ? formatTimestampForDateTime(
                          ordersFilters.createdAtAfter as string
                        )
                      : "",
                  },
                  to: {
                    id: "createdAtBefore",
                    name: "created_at_before",
                    value: ordersFilters.createdAtBefore
                      ? formatTimestampForDateTime(
                          ordersFilters.createdAtBefore as string
                        )
                      : "",
                  },
                }}
                onChangeDate={onChangeDateRangeFilter}
              />
            </div>
            <DateRange
              id="completedAt"
              label="Completed At"
              values={{
                from: {
                  id: "completedAtAfter",
                  name: "completed_at_after",
                  value: ordersFilters.completedAtAfter as string,
                },
                to: {
                  id: "completedAtBefore",
                  name: "completed_at_before",
                  value: ordersFilters.completedAtBefore as string,
                },
              }}
              onChangeDate={onChangeDateRangeFilter}
            />
            <DateRange
              id="cancelledAt"
              label="Cancelled At"
              values={{
                from: {
                  id: "cancelledAtAfter",
                  name: "cancelled_at_after",
                  value: ordersFilters.cancelledAtAfter as string,
                },
                to: {
                  id: "cancelledAtBefore",
                  name: "cancelled_at_before",
                  value: ordersFilters.cancelledAtBefore as string,
                },
              }}
              onChangeDate={onChangeDateRangeFilter}
            />
            {departments.length > 0 && (
              <SelectInput
                id="department"
                label="Department"
                defaultLabel="All Departments..."
                value={ordersFilters.department as string}
                options={departments.map((department: DepartmentWithLab) => ({
                  label: `${department.lab}: ${department.name}`,
                  value: String(department.id),
                }))}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  onChangeFilter("department", e.target.value)
                }
              />
            )}
          </div>
        </div>
        <div className="AdminFilters__actions AdminFilters__actions--content">
          <div className="AdminFilters__actions--content">
            <ControlledToggleButton
              isToggled={queryFilters.isUnfulfilled}
              onClick={() => {
                onChangeQueryFilter("isUnfulfilled");
              }}
            >
              Unfulfilled Orders
            </ControlledToggleButton>
            {canPrintLabOrders && (
              <ControlledToggleButton
                isToggled={queryFilters.isNotPrinted}
                onClick={() => {
                  onChangeQueryFilter("isNotPrinted");
                }}
              >
                Unprinted Orders
              </ControlledToggleButton>
            )}
          </div>
          <div className="AdminFilters__actions--content">
            <PathologyWatchButton
              type="submit"
              color="green"
              title="filter cases"
            >
              Filter Orders
            </PathologyWatchButton>
            <PathologyWatchButton onClick={onResetFilter} title="reset filters">
              Reset Filters
            </PathologyWatchButton>
          </div>
        </div>
      </form>
      {adminPaginationProps && (
        <div className={isLoading ? "AdminCaselist--loading" : undefined}>
          <AdminPagination {...adminPaginationProps}>
            {canPrintLabOrders && (
              <PathologyWatchButton
                onClick={() => submitOrdersToPrint()}
                disabled={
                  !(
                    adminPaginationProps.totalCount &&
                    filledFilters &&
                    ranFilters.current
                  )
                }
              >
                <>Print {adminPaginationProps.totalCount || 0} orders</>
              </PathologyWatchButton>
            )}
          </AdminPagination>
        </div>
      )}
    </>
  );
};

export default OrdersFilters;
